import { useState, useRef, useEffect, useCallback } from 'react'
import { Aircraft } from '../../types/intellijetnet'
import { AircraftTransaction, HiddenTransaction } from '../../types/database'
import { ConsolidatedTransaction } from '../../utils/transaction-consolidation'
import { HiddenTransactionsService } from '../../services/hidden-transactions.service'
import { formatDate } from '../../utils/formatters'

interface SoldEntry {
  aircraft: Aircraft
  transaction: ConsolidatedTransaction | null
  aircraftTransaction?: AircraftTransaction | null
}

interface HiddenTransactionsDropdownProps {
  modelId: number
  soldData: SoldEntry[]
  hiddenTransactions: HiddenTransaction[]
  onHide: (identifier: string) => Promise<void>
  onUnhide: (identifier: string) => Promise<void>
}

export default function HiddenTransactionsDropdown({
  modelId: _modelId,
  soldData,
  hiddenTransactions,
  onHide,
  onUnhide
}: HiddenTransactionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const hiddenCount = hiddenTransactions.length
  const hiddenIdentifiers = new Set(hiddenTransactions.map(h => h.transaction_identifier))

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  const getIdentifier = useCallback((entry: SoldEntry): string | null => {
    return HiddenTransactionsService.getTransactionIdentifier(
      entry.aircraftTransaction,
      entry.transaction
    )
  }, [])

  // Search visible (non-hidden) sold entries by serial number
  const searchResults = searchQuery.trim().length > 0
    ? soldData.filter(entry => {
        const id = getIdentifier(entry)
        if (!id || hiddenIdentifiers.has(id)) return false
        const sn = entry.aircraft.sernbr || ''
        return sn.toLowerCase().includes(searchQuery.toLowerCase())
      }).slice(0, 10)
    : []

  // Build display info for hidden entries
  const hiddenEntries = hiddenTransactions.map(ht => {
    // Try to find matching sold entry for display info
    const match = soldData.find(entry => {
      const id = getIdentifier(entry)
      return id === ht.transaction_identifier
    })
    return {
      ...ht,
      sernbr: match?.aircraft.sernbr || ht.transaction_identifier,
      regnbr: match?.aircraft.regnbr || null,
      dateSold: match?.aircraftTransaction?.transaction_date || match?.transaction?.trans_date || null
    }
  })

  const handleHide = async (identifier: string) => {
    setLoadingId(identifier)
    try {
      await onHide(identifier)
    } finally {
      setLoadingId(null)
    }
  }

  const handleUnhide = async (identifier: string) => {
    setLoadingId(identifier)
    try {
      await onUnhide(identifier)
    } finally {
      setLoadingId(null)
    }
  }

  const getEntryLabel = (sernbr: string | null, regnbr: string | null, dateSold: string | null) => {
    const sn = sernbr || 'Unknown S/N'
    const reg = regnbr ? ` (${regnbr})` : ''
    const date = dateSold ? ` — ${formatDate(dateSold)}` : ''
    return `${sn}${reg}${date}`
  }

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
      >
        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
        </svg>
        Hidden Sold
        {hiddenCount > 0 && (
          <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            {hiddenCount} hidden
          </span>
        )}
        <svg className={`ml-1.5 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
          <div className="p-3 space-y-3">
            {/* Search input */}
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by serial number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />

            {/* Search results */}
            {searchQuery.trim().length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Search Results</p>
                {searchResults.length === 0 ? (
                  <p className="text-sm text-gray-400 italic py-1">No matching transactions</p>
                ) : (
                  <ul className="max-h-40 overflow-y-auto space-y-1">
                    {searchResults.map((entry, idx) => {
                      const id = getIdentifier(entry)
                      if (!id) return null
                      const dateSold = entry.aircraftTransaction?.transaction_date || entry.transaction?.trans_date || null
                      return (
                        <li key={id || idx} className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-50 text-sm">
                          <span className="truncate mr-2 text-gray-700">
                            {getEntryLabel(entry.aircraft.sernbr, entry.aircraft.regnbr, dateSold)}
                          </span>
                          <button
                            onClick={() => handleHide(id)}
                            disabled={loadingId === id}
                            className="flex-shrink-0 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 transition-colors"
                          >
                            {loadingId === id ? '...' : 'Hide'}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-200" />

            {/* Hidden transactions list */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                Hidden Transactions ({hiddenCount})
              </p>
              {hiddenEntries.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-1">No hidden transactions</p>
              ) : (
                <ul className="max-h-48 overflow-y-auto space-y-1">
                  {hiddenEntries.map((entry) => (
                    <li key={entry.id} className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-50 text-sm">
                      <span className="truncate mr-2 text-gray-700">
                        {getEntryLabel(entry.sernbr, entry.regnbr, entry.dateSold)}
                      </span>
                      <button
                        onClick={() => handleUnhide(entry.transaction_identifier)}
                        disabled={loadingId === entry.transaction_identifier}
                        className="flex-shrink-0 text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 transition-colors"
                      >
                        {loadingId === entry.transaction_identifier ? '...' : 'Unhide'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
